export const convertISOToCustomFormat = (isoDate: string) => {
  return isoDate && isoDate.replace('T', ' ').replace('Z', ' UTC');
};

export const getStatusColor = (status: string) => {
  if (status?.toUpperCase() === 'QUEUED') {
    return '#0fecf5';
  } else if (status?.toUpperCase() === 'COMPLETED') {
    return '#34C700';
  } else if (status?.toUpperCase() === 'IN_PROGRESS') {
    return 'var(--color-prime-400)';
  } else if (status?.toUpperCase() === 'ERROR') {
    return '#FF4F37';
  }
};

/**
 * Friendly names for the photo-edit operations, so an AI IMAGE EDIT job says
 * *which* edit it was. The operation is the one thing that distinguishes two
 * otherwise identical rows in the job list.
 */
const PHOTO_EDIT_OPERATIONS: Record<string, string> = {
  auto_enhance: 'Auto Enhance',
  ai_restore: 'AI Restore',
  sequence: 'Custom enhancement',
  remove_object: 'Remove object',
  remove_background: 'Background removal',
  white_balance: 'White balance',
  exposure_correction: 'Exposure',
  color: 'Colour',
  deblur: 'Sharpen',
  upscale: 'Upscale',
  face_restore: 'Face restore',
};

/**
 * The job's display label, qualified by what it actually did where that is
 * knowable: "AI IMAGE EDIT - Auto Enhance" rather than a row indistinguishable
 * from every other photo edit.
 */
export const describe_job = (type: string, input?: any): string => {
  // get_job_map_value returns null for anything it does not know, which is why
  // a new job type renders as a blank label. Fall back to the raw type: ugly
  // beats invisible, and it stops the next job type shipping nameless.
  const label = get_job_map_value(type) || type || '';
  if (type === 'PHOTO_EDIT') {
    const op = input?.operation;
    const opLabel = op ? PHOTO_EDIT_OPERATIONS[op] || op : null;
    if (opLabel) return `${label} - ${opLabel}`;
  }
  return label;
};

export const get_job_map_value = (key: string) => {
  switch (key) {
    case 'BULK_DELETE_ASSETS': {
      return 'BULK PURGE ASSETS';
    }
    case 'PURGE_ASSETS': {
      return 'PURGE ASSETS';
    }
    case 'VIDEO_TRANSCODE': {
      return 'VIDEO TRANSCODE';
    }
    case 'PHOTO_EDIT': {
      return 'AI IMAGE EDIT';
    }
    case 'PRINT_EXPORT': {
      return 'PRINT EXPORT';
    }
    case 'BULK_CUTOUT': {
      return 'BACKGROUND REMOVAL';
    }
    case 'COMPLETED': {
      return 'COMPLETED';
    }
    case 'ERROR': {
      return 'ERROR';
    }
    case 'QUEUED': {
      return 'QUEUED';
    }
    case 'IN_PROGRESS': {
      return 'PROCESSING';
    }
    default: {
      return null;
    }
  }
};

export function convertSeconds(seconds: number) {
  const roundedMinutes = Math.ceil(seconds / 60);
  return `${roundedMinutes}m`;
}

/**
 * Byte formatter for **binary** units (1 KB = 1024) — file sizes on disk and
 * memory, where 1024-based units are what the OS reports and what users expect.
 *
 * If the number relates to money — anything the backend meters and invoices —
 * use `formatBillingBytes` instead; see the rule in its comment below.
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Byte formatter for BILLING surfaces — decimal units (1 GB = 10^9), not the
 * binary ones `formatBytes` uses.
 *
 * The backend meters and invoices storage and CDN delivery in decimal GB
 * (`recorder.BYTES_PER_GB = 10**9`, plan-structure v2 Phase 2). Rendering
 * those numbers with 1024-based units makes every figure on the billing page
 * read ~7% under what the customer is actually billed for: a 1 GB allowance
 * showed as "953.67 MB" and a 150 GB delivery allowance as "139.7 GB".
 *
 * **The rule:** does this number appear on an invoice, or against an allowance
 * the customer is charged for exceeding? Then decimal (this function). Is it a
 * file on disk or a memory figure? Then binary (`formatBytes`).
 *
 * Applied to (swept 2026-08-07, plan-structure v1.2 Phase 8):
 *   - Subscription page — quotas, usage, rate card.
 *   - `/usage` + usage details — Storage, CDN Usage, Data Transferred.
 *   - Delivery Insights — every `bytes` column and total; that traffic IS the
 *     billed `cdn_bytes` dimension.
 *
 * Deliberately NOT applied to Teleport agent stats: `ram_used_bytes` /
 * `ram_total_bytes` are memory (binary is correct), and the agent's transfer
 * counters are diagnostics, not a billed dimension.
 */
export function formatBillingBytes(bytes: number, decimals: number = 2): string {
  if (!bytes) return '0 Bytes';
  const k = 1000;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(k)), sizes.length - 1);
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export function dateToUnixTimestamp(dateString: string): number {
  return Math.floor(new Date(dateString).getTime() / 1000);
}

export function sliceKeyForApiUsage(key: string) {
  const suffix = key.split('/').pop();
  if (suffix === '{JOB_ID}') {
    const basePath =
      key.split('/api/v1/assets/addons')[1] ||
      key.split('/api/v1/assets/ASSETS/addons')[1] ||
      key.split('/api/v1/assets/{ASSET_ID}/addons')[1];

    if (basePath) {
      const cleanedPath = basePath.replace('/jobs', '');
      return cleanedPath;
    }
  }
  return suffix || '';
}

export function formatNumberIntl(value: any) {
  return new Intl.NumberFormat('en-GB').format(value);
}

export const validateDateRange = (startDate: string, endDate: string): object => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  let res = { status: false, message: '' };
  // start > end?
  if (start > end) {
    res = { status: true, message: 'The start date must be before the end date.' };
    return res;
  }

  // start === end?
  if (start.getTime() === end.getTime()) {
    res = { status: true, message: 'Start and end dates cannot be the same.' };
    return res;
  }
  
  const oneWeek = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
  if (Math.abs(end.getTime() - start.getTime()) < oneWeek) {
    res = { status: true, message: 'Start and end dates must be at least a week apart.' };
    return res;
  }
  

  return res;
};

export const minDate = (() => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  d.setDate(d.getDate() + 1);
  return d.toISOString().split('T')[0];
})();

export const maxDate = new Date()
  .toISOString()
  .split('T')[0];


export const assetCalc=(totalAssets: number, imageAsset: number, videoAsset: number) => {

     const otherAssets = totalAssets - (imageAsset + videoAsset);

     return Math.abs(otherAssets);
}

export function dateDiffInDays(a:any, b:any) {
  const _MS_PER_DAY = 1000 * 60 * 60 * 24;
  // Discard the time and time-zone information.
  const utc1 = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utc2 = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());

  return Math.floor((utc2 - utc1) / _MS_PER_DAY);
}

export function formatDateTimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

export function formatToLocal(dateStr?: string): string {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
  }).replace(',', '');
}
